import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InboxEntry, UpdateInboxEntryInput } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useUpdateInboxEntry } from './useUpdateInboxEntry';
import { useUnreadStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

export const useAcceptSuggestion = () => {
  const { t } = useTranslation();
  const { handleUpdateInboxEntry } = useUpdateInboxEntry();
  const { updateInboxEntry } = useActiveInboxEntriesSlice();
  const { promiseToast } = useCustomToast();
  const { markAsRead } = useUnreadStore();
  const [isAccepting, setIsAccepting] = useState(false);

  const acceptSuggestion = useCallback(
    async (
      inboxEntryId: string,
      currentEntry: InboxEntry,
    ): Promise<boolean> => {
      setIsAccepting(true);

      const acceptPromise = (async () => {
        try {
          const updateData: UpdateInboxEntryInput = {
            id: inboxEntryId,
            isSuggestion: false,
          };

          const updatedEntry = await handleUpdateInboxEntry(updateData);

          if (updatedEntry) {
            updateInboxEntry(updatedEntry);
            markAsRead(inboxEntryId, updatedEntry.taskGroupId);
            log.info(`Suggestion accepted for inbox entry ${inboxEntryId}`);
            return true;
          }

          throw new Error('Failed to update inbox entry');
        } catch (error) {
          updateInboxEntry(currentEntry);
          log.error('Error accepting suggestion:', error);
          throw error;
        } finally {
          setIsAccepting(false);
        }
      })();

      const didAccept = await promiseToast(
        acceptPromise,
        t('doBox.actions.acceptingSuggestion'),
        t('doBox.actions.acceptSuggestionSuccess'),
        t('doBox.actions.acceptSuggestionError'),
      );

      return didAccept === true;
    },
    [handleUpdateInboxEntry, updateInboxEntry, promiseToast, t, markAsRead],
  );

  return {
    acceptSuggestion,
    isAccepting,
  };
};
